/**
 * Quixo OS support assistant, phase 1: authenticated, read-only, source-backed Help Centre lookup.
 * NOT generative AI, NOT a ticket system, and NOT an execution surface.
 * The server derives tenant and actor solely from the signed session.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { bff } from '../lib/bff';
import { useApp } from '../lib/app-context';
import { moduleFlags, useFlags } from '../lib/flags';
import { Icon } from './Icon';
import { canSearchSupport, parseSupportKnowledgeHits, supportKnowledgePath, supportSearchPath, type SupportKnowledgeHit } from './support-search';
import type { ShellSurface } from './Shell';
import './support-assistant.css';

type HelpMatch = { id: string; title: string; excerpt?: string; category?: string; fallback?: boolean; locale?: string };
type HelpArticle = { id: string; title: string; body: string; locale?: string; fallback?: boolean };
type AiReply = { reply: string; generated: boolean; sources: { id: string; title: string; fallback?: boolean }[] };
type Turn = { id: number; idempotencyKey: string; question: string; matches: HelpMatch[]; knowledge?: SupportKnowledgeHit[]; knowledgeFailed?: boolean; failed?: boolean; ai?: AiReply; aiFailed?: boolean };

const SUPPORT_EMAIL = 'support@quixomarketing.com';
const SUPPORT_WHATSAPP = 'https://wa.me/971501022877';

const SUPPORT_COPY_KEYS = ["open","title","subtitle","intro","ask","placeholder","send","close","clear","searching","empty","failure","results","source","back","fallback","staff","human","email","whatsapp","caution","disclaimer","privateSearch","knowledgeTitle","knowledgeProblem","knowledgeRevoked","explain","explaining","aiNotice","aiFailed","aiSources"] as const;

export function SupportAssistant({ surface }: { surface: ShellSurface }) {
  const { locale, me, t: translate } = useApp();
  const lang = locale === 'ar' ? 'ar' : 'en';
  const t = Object.fromEntries(SUPPORT_COPY_KEYS.map((key) => [key, translate('shell.support.' + key)])) as Record<(typeof SUPPORT_COPY_KEYS)[number], string>;
  const enabled = canSearchSupport(surface, me?.tenantId);
  const flags = useFlags(surface === 'customer');
  const canSearchKnowledge = enabled && moduleFlags('autorag').some((key) => flags(key) === 'on');
  const canGenerate = enabled && flags('support.ai') === 'on';
  const scope = surface + ':' + (me?.tenantId ?? '') + ':' + (me?.email ?? '');
  const scopeRef = useRef(scope);
  scopeRef.current = scope;
  const counter = useRef(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [includeKnowledge, setIncludeKnowledge] = useState(false);
  const [busy, setBusy] = useState(false);
  const [replyBusyId, setReplyBusyId] = useState<number | null>(null);
  const [turns, setTurns] = useState<Turn[]>([]);
  const [article, setArticle] = useState<HelpArticle | null>(null);
  const [articleBusy, setArticleBusy] = useState(false);
  const [revokedSource, setRevokedSource] = useState(false);

  useEffect(() => {
    setTurns([]);
    setArticle(null);
    setQuery('');
    setIncludeKnowledge(false);
    setBusy(false);
    setReplyBusyId(null);
    setArticleBusy(false);
    setRevokedSource(false);
    setOpen(false);
  }, [scope]);

  useEffect(() => {
    if (open && !article) inputRef.current?.focus();
  }, [open, article]);

  if (!me) return null;

  function close() {
    setOpen(false);
    setArticle(null);
    triggerRef.current?.focus();
  }

  async function search(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = query.trim();
    if (!enabled || busy || text.length < 2) return;
    const requestScope = scope;
    const id = ++counter.current;
    const idempotencyKey = globalThis.crypto?.randomUUID?.() ?? `support-${Date.now()}-${id}`;
    setQuery('');
    setBusy(true);
    const searchPrivate = includeKnowledge && canSearchKnowledge;
    try {
      const [help, knowledge] = await Promise.all([
        bff<HelpMatch[]>(supportSearchPath(text, lang))
          .then((matches) => ({ matches: matches.slice(0, 5), failed: false }),
            () => ({ matches: [] as HelpMatch[], failed: true })),
        searchPrivate
          ? bff<unknown>(supportKnowledgePath(text))
              .then((payload) => ({ hits: parseSupportKnowledgeHits(payload), failed: false }),
                () => ({ hits: [] as SupportKnowledgeHit[], failed: true }))
          : Promise.resolve({ hits: [] as SupportKnowledgeHit[], failed: false }),
      ]);
      if (scopeRef.current === requestScope) {
        setTurns((prev) => [...prev, {
          id, idempotencyKey, question: text, matches: help.matches, knowledge: knowledge.hits,
          knowledgeFailed: searchPrivate && knowledge.failed,
          failed: help.failed && (!searchPrivate || knowledge.failed),
        }]);
      }
    } finally {
      if (scopeRef.current === requestScope) setBusy(false);
    }
  }

  async function explain(turn: Turn) {
    if (!canGenerate || replyBusyId !== null || turn.ai) return;
    const requestScope = scope;
    setReplyBusyId(turn.id);
    try {
      const answer = await bff<AiReply>('/v1/ai/support/reply', {
        method: 'POST',
        body: JSON.stringify({ question: turn.question, locale: lang }),
        headers: { 'idempotency-key': turn.idempotencyKey },
      });
      if (scopeRef.current === requestScope)
        setTurns((prev) => prev.map((t) => t.id === turn.id ? { ...t, ai: answer, aiFailed: false } : t));
    } catch {
      if (scopeRef.current === requestScope)
        setTurns((prev) => prev.map((t) => t.id === turn.id ? { ...t, aiFailed: true } : t));
    } finally {
      if (scopeRef.current === requestScope) setReplyBusyId(null);
    }
  }

  async function readArticle(id: string) {
    const requestScope = scope;
    setArticleBusy(true);
    try {
      const path = ('/v1/help-centre/articles/' + encodeURIComponent(id) + '?locale=' + lang) as `/v1/${string}`;
      const found = await bff<HelpArticle>(path);
      if (scopeRef.current === requestScope) setArticle(found);
    } catch {
      if (scopeRef.current === requestScope) setArticle(null);
    } finally {
      if (scopeRef.current === requestScope) setArticleBusy(false);
    }
  }

  async function readKnowledge(hit: SupportKnowledgeHit) {
    const requestScope = scope;
    setRevokedSource(false);
    setArticleBusy(true);
    try {
      const path = ('/v1/auto-rag/documents/' + encodeURIComponent(hit.sourceId) + '/source') as `/v1/${string}`;
      const source = await bff<{ id: string; title: string; body: string; version: number }>(path);
      if (source.id !== hit.sourceId || source.version !== hit.sourceVersion) throw new Error('source_changed');
      if (scopeRef.current === requestScope) setArticle({id:source.id,title:source.title,body:source.body});
    } catch {
      if (scopeRef.current === requestScope) {
        // A deletion or revision may occur after search. Do not keep stale source snippets in UI state.
        setTurns((prev) => prev.map((t) => ({...t,knowledge:t.knowledge?.filter((h) => h.sourceId !== hit.sourceId)})));
        setRevokedSource(true);
        setArticle(null);
      }
    } finally {
      if (scopeRef.current === requestScope) setArticleBusy(false);
    }
  }

  return (
    <div className="qm-support" dir={lang === 'ar' ? 'rtl' : 'ltr'}>
      {open && (
        <section id="qm-support-panel" className="qm-support__panel" role="dialog" aria-label={t.title}
          onKeyDown={(e) => { if (e.key === 'Escape') { e.stopPropagation(); close(); } }}>
          <div className="qm-support__head">
            <div><strong>{t.title}</strong><small>{t.subtitle}</small></div>
            <button type="button" className="qm-support__icon" onClick={close} aria-label={t.close}><Icon name="close" size={18} /></button>
          </div>
          <div className="qm-support__body" role="region" aria-label={t.results}>
            {article ? (
              <article className="qm-support__article">
                <button type="button" className="qm-support__inline" onClick={() => setArticle(null)}>{t.back}</button>
                <h3>{article.title}</h3>
                {article.fallback && <p className="qm-support__note">{t.fallback}</p>}
                <p className="qm-support__article-body">{article.body}</p>
              </article>
            ) : (
              <>
                <p className="qm-support__intro">{enabled ? t.intro : t.staff}</p>
                {revokedSource && <p className="qm-support__note" role="status">{t.knowledgeRevoked}</p>}
                <div aria-live="polite">
                  {turns.map((turn) => (
                    <div className="qm-support__turn" key={turn.id}>
                      <p className="qm-support__question">{turn.question}</p>
                      {turn.failed ? <p className="qm-support__note">{t.failure}</p> :
                        turn.matches.length === 0 && !turn.knowledge?.length ? <p className="qm-support__note">{t.empty}</p> :
                        <>
                          {turn.matches.length > 0 && <div className="qm-support__sources">
                            <strong>{t.results}</strong>
                            {turn.matches.map((match) => (
                              <button key={match.id} type="button" className="qm-support__source" disabled={articleBusy}
                                onClick={() => void readArticle(match.id)}>
                                <span>{match.title}</span>
                                {match.excerpt && <small>{match.excerpt}</small>}
                                {match.fallback && <small>{t.fallback}</small>}
                                <span className="qm-support__inline">{t.source}</span>
                              </button>
                            ))}
                          </div>}
                          {!!turn.knowledge?.length && <div className="qm-support__sources">
                            <strong>{t.knowledgeTitle}</strong>
                            {turn.knowledge.map((hit) => (
                              <button type="button" key={hit.sourceId + ':' + hit.sourceVersion} className="qm-support__source"
                                disabled={articleBusy} onClick={() => void readKnowledge(hit)}>
                                <span>{hit.label}</span><small className="qm-support__passage">{hit.text}</small>
                                <span className="qm-support__inline">{t.source}</span>
                              </button>
                            ))}
                          </div>}
                        </>
                      }
                      {turn.knowledgeFailed && <p className="qm-support__note">{t.knowledgeProblem}</p>}
                      {canGenerate && turn.matches.length > 0 && !turn.ai && <button type="button" className="qm-support__explain" onClick={() => void explain(turn)} disabled={replyBusyId !== null}>
                        <Icon name="sparkle" size={15} /> {replyBusyId === turn.id ? t.explaining : t.explain}
                      </button>}
                      {turn.aiFailed && <p className="qm-support__note" role="alert">{t.aiFailed}</p>}
                      {turn.ai && <div className="qm-support__answer">
                        <p>{turn.ai.reply}</p>
                        {turn.ai.generated && <p className="qm-support__note">{t.aiNotice}</p>}
                        {turn.ai.sources.length > 0 && <div className="qm-support__sources">
                          <strong>{t.aiSources}</strong>
                          {turn.ai.sources.map((source) => <button key={source.id} type="button" className="qm-support__source" disabled={articleBusy} onClick={() => void readArticle(source.id)}>
                            <span>{source.title}</span><span className="qm-support__inline">{t.source}</span>
                          </button>)}
                        </div>}
                      </div>}
                    </div>
                  ))}
                  {busy && <p className="qm-support__note" role="status">{t.searching}</p>}
                </div>
              </>
            )}
          </div>
          {!article && (
            <form className="qm-support__form" onSubmit={(e) => void search(e)}>
              {canSearchKnowledge && <label className="qm-support__knowledge-toggle">
                <input type="checkbox" checked={includeKnowledge} onChange={(e) => setIncludeKnowledge(e.target.checked)} disabled={busy} />
                <span>{t.privateSearch}</span>
              </label>}
              <label htmlFor="qm-support-input">{t.ask}</label>
              <div className="qm-support__input-row">
                <input id="qm-support-input" ref={inputRef} value={query} onChange={(e) => setQuery(e.target.value)}
                  placeholder={t.placeholder} minLength={2} maxLength={200} disabled={!enabled || busy} />
                <button type="submit" disabled={!enabled || busy || query.trim().length < 2}>{t.send}</button>
              </div>
              {turns.length > 0 && <button type="button" className="qm-support__inline" onClick={() => { setTurns([]); setQuery(''); }}>{t.clear}</button>}
            </form>
          )}
          <div className="qm-support__foot">
            <strong>{t.human}</strong>
            <div className="qm-support__links">
              {enabled && <a href="/help">{translate('shell.supportCases.title')}</a>}
              <a href={'mailto:' + SUPPORT_EMAIL}>{t.email}</a>
              <a href={SUPPORT_WHATSAPP} target="_blank" rel="noopener noreferrer">{t.whatsapp}</a>
            </div>
            <p>{t.caution}</p><p>{t.disclaimer}</p>
          </div>
        </section>
      )}
      <button ref={triggerRef} type="button" className="qm-support__trigger" aria-expanded={open}
        aria-controls="qm-support-panel" aria-label={open ? t.close : t.open} onClick={() => open ? close() : setOpen(true)}>
        <Icon name="headset" size={19} /><span>{t.title}</span>
      </button>
    </div>
  );
}
